import { AsyncPipe, NgClass, NgIf, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { TimezoneService } from '@sc/data-access-configuration';
import {
  Dossier,
  Fitter,
  FitterServiceType,
  FitterVehicle,
  LocationDataGeocoded,
  LoggerLevelType,
} from '@sc/data-access-models';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  IconComponent,
  InputTextComponent,
  MomentParseZonePipe,
  SelectComponent,
} from '@sc/ui-components';
import { dayjs, getPeriodReplacementVehicleUse } from '@sc/utils';
import { of } from 'rxjs';
import { delay, filter, map, scan, startWith } from 'rxjs/operators';
import { PlanningSandboxService } from '../../../services';
import { PlanningFormAddressFieldComponent } from '../planning-form-address-field/planning-form-address-field.component';

@Component({
  selector: 'sc-planning-form-appointment-mobility',
  styleUrls: ['./planning-form-appointment-mobility.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    NgIf,
    MatFormFieldModule,
    MatRadioModule,
    PlanningFormAddressFieldComponent,
    MatSlideToggleModule,
    IconComponent,
    MatInputModule,
    MatDatepickerModule,
    InputTextComponent,
    NgClass,
    AsyncPipe,
    TitleCasePipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
    SelectComponent,
  ],
  template: `
    <ng-container [formGroup]="getAppointmentForm()">
      <fieldset>
        <legend>
          {{ 'PlanningFormAppointmentMobility.title' | translate }}
        </legend>
        <div
          class="form-row"
          *ngIf="canDoInCenterIntervention() || canDoOutsideIntervention()"
        >
          <div class="form-data">
            <mat-label class="title text-grey-dark asterisk">{{
              'PlanningFormAppointmentMobility.outsideInterventionLabel'
                | translate
            }}</mat-label>
            <mat-radio-group
              (change)="outsideInterventionChange($event)"
              [disabled]="form?.disabled"
            >
              <mat-radio-button
                *ngIf="canDoInCenterIntervention()"
                [value]="false"
                [checked]="getInterventionTypeValue() === 'garageIntervention'"
                ><span class="sc-design-text-3">{{
                  'PlanningFormAppointmentMobility.garageIntervention'
                    | translate
                }}</span>
              </mat-radio-button>
              <mat-radio-button
                [checked]="getInterventionTypeValue() === 'outsideIntervention'"
                *ngIf="canDoOutsideIntervention()"
                [value]="true"
                ><span class="sc-design-text-3">{{
                  'PlanningFormAppointmentMobility.outsideIntervention'
                    | translate
                }}</span>
              </mat-radio-button>
              <mat-radio-button
                *ngIf="canDoConveyingIntervention()"
                [value]="'convoyage'"
                [checked]="
                  getInterventionTypeValue() === 'convoyageIntervention'
                "
                ><span class="sc-design-text-3">{{
                  'PlanningFormAppointmentMobility.convoyageIntervention'
                    | translate
                }}</span>
              </mat-radio-button>
            </mat-radio-group>
          </div>
        </div>

        <div *ngIf="displayAddressForm()" class="form-row grey-bg">
          <div class="form-data">
            <h3 class="asterisk">
              {{
                'PlanningFormAppointmentMobility.addressChoiceTitle' | translate
              }}
            </h3>
            <sc-planning-form-address-field
              [searchLocationData]="this.getInterventionLocationForm().value"
              [disabled]="form?.disabled"
              (changeAddress)="onChangeAddress($event)"
            ></sc-planning-form-address-field>
          </div>
        </div>

        <div class="form-row" *ngIf="canShareReplacementVehicle()">
          <div class="form-data">
            <div class="replacement-vehicle-filter">
              <div class="filters-summary-rv">
                <mat-slide-toggle
                  [disableRipple]="true"
                  [hideIcon]="true"
                  (change)="onChangeReplacementVehicle($event?.checked)"
                  formControlName="isReplacementVehicleSelected"
                ></mat-slide-toggle>
                <sc-icon name="car"></sc-icon>
                <span class="filter-rv-text">{{
                  'PlanningFormAppointmentMobility.interventionLabel'
                    | translate
                }}</span>
              </div>
            </div>
          </div>
        </div>
        <div
          class="form-row grey-bg replacement-vehicle-form-container"
          *ngIf="
            !!getIsReplacementVehicleSelectedForm()?.value &&
            canShareReplacementVehicle()
          "
        >
          <div class="form-data">
            <div
              *ngIf="displayReplacementVehicleNeededAlert()"
              class="replacement-vehicle-alert"
            >
              <div class="replacement-vehicle-alert-container">
                <span class="replacement-vehicle-alert-title">{{
                  'PlanningFormAppointment.replacementVehicleNeededTitle'
                    | translate
                }}</span>
                <span
                  >{{
                    'PlanningFormAppointment.replacementVehicleNeededText'
                      | translate
                  }}
                </span>
              </div>
            </div>

            <div class="rv-use-date-row">
              <div class="rv-use-date-select-container">
                <mat-label class="title text-grey-dark">{{
                  'PlanningFormAppointmentMobility.retrieveRVDate' | translate
                }}</mat-label>
                <mat-form-field>
                  <input
                    matInput
                    (focus)="inputDatePickerStart.click()"
                    (click)="inputDatePickerStart.click()"
                    [value]="
                      getRVUseStart().format()
                        | localeDate: 'fullDate'
                        | async
                        | titlecase
                    "
                    [disabled]="form?.disabled"
                    readonly
                  />

                  <input
                    class="hidden-input"
                    #inputDatePickerStart
                    matInput
                    [value]="getRVUseStart().format()"
                    [matDatepicker]="pickerStart"
                    [disabled]="form?.disabled"
                    (focus)="pickerStart.open()"
                    (click)="pickerStart.open()"
                    (dateChange)="onRVUseDateStartChange($event)"
                    readonly
                  />
                  <sc-icon matSuffix name="arrow_bottom"></sc-icon>

                  <mat-datepicker
                    #pickerStart
                    panelClass="rv-start-use-date-picker-calendar"
                  ></mat-datepicker>
                </mat-form-field>
              </div>
              <div class="rv-use-date-select-container">
                <mat-label class="title text-grey-dark">{{
                  'PlanningFormAppointmentMobility.retrieveRVTime' | translate
                }}</mat-label>
                <sc-input-text
                  appearance="fill"
                  [value]="
                    getRVUseStart().format()
                      | parseZoneTimeString: _fitter?.timezone
                      | localeDate: 'HH:mm'
                      | async
                  "
                  rightIcon="arrow_bottom"
                  [clickableIcon]="true"
                  [suggestions]="generateRVTimeSuggestions()"
                  [scrollToSuggestion]="getRVUseStart().format('HH:mm')"
                  [forceOptionSelectedOnEnter]="false"
                  [disabled]="form?.disabled"
                  (change)="onRVUseHourStartChange($any($event?.target)?.value)"
                  (selectSuggestion)="onRVUseHourStartChange($event)"
                ></sc-input-text>
              </div>
            </div>
            <div class="rv-use-date-row">
              <div class="rv-use-date-select-container">
                <mat-label class="title text-grey-dark">{{
                  'PlanningFormAppointmentMobility.depositRVDate' | translate
                }}</mat-label>
                <mat-form-field>
                  <input
                    matInput
                    (focus)="inputDatePickerEnd.click()"
                    (click)="inputDatePickerEnd.click()"
                    [value]="
                      getRVUseEnd().format()
                        | localeDate: 'fullDate'
                        | async
                        | titlecase
                    "
                    [disabled]="form?.disabled"
                    readonly
                  />

                  <input
                    class="hidden-input"
                    #inputDatePickerEnd
                    matInput
                    [value]="getRVUseEnd().format()"
                    [min]="getRVUseStart().format()"
                    [matDatepicker]="pickerEnd"
                    [disabled]="form?.disabled"
                    (focus)="pickerEnd.open()"
                    (click)="pickerEnd.open()"
                    (dateChange)="onRVUseDateEndChange($event)"
                    readonly
                  />
                  <sc-icon matSuffix name="arrow_bottom"></sc-icon>

                  <mat-datepicker
                    #pickerEnd
                    panelClass="rv-end-use-date-picker-calendar"
                  ></mat-datepicker>
                </mat-form-field>
              </div>
              <div class="rv-use-date-select-container">
                <mat-label class="title text-grey-dark">{{
                  'PlanningFormAppointmentMobility.depositRVTime' | translate
                }}</mat-label>
                <sc-input-text
                  appearance="fill"
                  [value]="
                    getRVUseEnd().format()
                      | parseZoneTimeString: _fitter?.timezone
                      | localeDate: 'HH:mm'
                      | async
                  "
                  rightIcon="arrow_bottom"
                  [clickableIcon]="true"
                  [suggestions]="generateRVEndTimeSuggestions()"
                  [scrollToSuggestion]="getRVUseEnd().format('HH:mm')"
                  [forceOptionSelectedOnEnter]="false"
                  [disabled]="form?.disabled"
                  (change)="onRVUseHourEndChange($any($event?.target)?.value)"
                  (selectSuggestion)="onRVUseHourEndChange($event)"
                ></sc-input-text>
              </div>
            </div>

            <mat-label class="title text-grey-dark asterisk">{{
              'PlanningFormAppointmentMobility.replacementVehicleLabel'
                | translate
            }}</mat-label>

            <sc-select
              class="assigned-replacement-vehicle-select"
              [value]="selectedReplacementVehicle"
              [options]="_fitter?.vehicles"
              (valueChange)="onReplacementVehicleSelect($event)"
              [disabledOptionFn]="isVehicleDisabled"
              [compareWithFn]="vehicleCompareWith"
              [placeholder]="
                'PlanningFormAppointmentMobility.notSelected' | translate
              "
              [disabled]="form?.disabled"
            >
              <ng-template #triggerTemplate let-item>
                <span class="vehicle-infos"
                  ><span class="vehicle-name">{{
                    getAssignedVehicleFormValue()?.name
                  }}</span>
                  <span class="vehicle-model"
                    >&nbsp;{{
                      getLicensePlateAndManufacturer(
                        getAssignedVehicleFormValue()
                      )
                    }}&nbsp;
                  </span>
                </span>
                <span class="replacement-vehicle">
                  <span
                    class="vehicle-availability"
                    [ngClass]="{
                      unavailable: !(
                        getAssignedVehicleFormValue()?.currentActive ??
                        getAssignedVehicleFormValue()?.active
                      ),
                      available: !!(
                        getAssignedVehicleFormValue()?.currentActive ??
                        getAssignedVehicleFormValue()?.active
                      )
                    }"
                  ></span>
                  {{
                    getAssignedVehicleFormValue()?.currentActive ??
                    getAssignedVehicleFormValue()?.active
                      ? ('PlanningFormAppointmentMobility.replacementVehicleAvailable'
                        | translate)
                      : ('PlanningFormAppointmentMobility.replacementVehicleUnavailable'
                        | translate)
                  }}
                </span>
              </ng-template>

              <ng-template #optionTemplate let-item>
                <ng-container *ngIf="item; else emptyItem">
                  <span class="vehicle-line">
                    <span class="vehicle-infos"
                      ><span class="vehicle-name">{{ item?.name }}</span>
                      <span class="vehicle-model"
                        >&nbsp;{{ getLicensePlateAndManufacturer(item) }}&nbsp;
                      </span>
                    </span>
                    <span class="replacement-vehicle">
                      <span
                        class="vehicle-availability"
                        [ngClass]="{
                          unavailable: item?.currentActive === false,
                          available: item?.currentActive === true
                        }"
                      ></span>
                      {{
                        item?.currentActive
                          ? ('PlanningFormAppointmentMobility.replacementVehicleAvailable'
                            | translate)
                          : ('PlanningFormAppointmentMobility.replacementVehicleUnavailable'
                            | translate)
                      }}
                    </span>
                  </span>
                </ng-container>
                <ng-template #emptyItem>
                  <span>{{
                    'PlanningFormAppointmentMobility.notSelected' | translate
                  }}</span>
                </ng-template>
              </ng-template>
            </sc-select>
            <div
              *ngIf="isSelectedReplacementVehicleError"
              class="replacement-vehicle-not-available-alert"
            >
              {{
                'PlanningFormAppointmentMobility.replacementVehicleNotAvailableErrorText'
                  | translate
              }}
            </div>
            <div
              *ngIf="isRVPeriodError()"
              class="replacement-vehicle-not-available-alert"
            >
              {{
                'PlanningFormAppointmentMobility.replacementVehicleUsePeriodErrorText'
                  | translate
              }}
            </div>
          </div>
        </div>
      </fieldset>
    </ng-container>
  `,
})
export class PlanningFormAppointmentMobilityComponent
  implements OnInit, OnChanges
{
  @Input() set initForm(formInput: FormGroup) {
    if (formInput) {
      this.form = formInput;

      this.selectedReplacementVehicle = this.getAssignedVehicleFormValue();

      this.changeDetector.markForCheck();
    }
  }

  @Input() set fitters(fitters: Fitter[]) {
    if (fitters?.length) {
      this._fitters = fitters;
      this.setCurrentFitter();
      this.changeDetector.markForCheck();
    }
  }

  @Input() set _dossier(dossier: Dossier) {
    this.dossier = dossier;
    if (this.dossier?.appointment || this.dossier?.requestAppointment) {
      this.lastMobilityManuallySelected = this.getInterventionTypeValue();
    }
  }
  dossier: Dossier;
  @Input() isEditMode: boolean;
  @Input() set touched(value) {
    if (value) this.form?.get('appointment').markAllAsTouched();
  }

  @Output() isSelectedReplacementVehicleErrorEvent: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  form: FormGroup;

  _fitters: Fitter[];
  _fitter: Fitter;

  selectedReplacementVehicle: FitterVehicle;
  isSelectedReplacementVehicleError: boolean;
  selectedAddressStr: string;
  isAddressInputError: boolean;

  currentIntervention: FitterServiceType;

  replacementVehicleNeeded = false;
  replacementVehicleSelected = false;

  replacementVehicleTimeSuggestions = this.generateRVTimeSuggestions();

  lastMobilityManuallySelected: MobilityType = undefined;

  constructor(
    public translateService: TranslatorService,
    private readonly planningSandbox: PlanningSandboxService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly timezoneService: TimezoneService,
    private readonly translator: TranslatorService,
    private readonly snackbarService: SnackbarService
  ) {}

  ngOnInit() {
    this.form
      ?.get('appointment')
      ?.get('fitterId')
      ?.valueChanges?.subscribe(() => {
        this.setCurrentFitter();
        this.resetAppointmentType();

        this.getAssignedVehicleForm()?.setValue(null);
        this.changeDetector.markForCheck();
      });

    this.form
      ?.get('appointment')
      ?.get('endTime')
      ?.valueChanges?.subscribe(() => {
        this.updateFitterVehiclesAvailabilities();
        this.resetAssignedRVUse();
        this.changeDetector.markForCheck();
      });
    this.form
      ?.get('appointment')
      ?.get('time')
      ?.valueChanges?.subscribe(() => {
        this.updateFitterVehiclesAvailabilities();
        this.resetAssignedRVUse();
        this.changeDetector.markForCheck();
      });

    this.form
      .get('appointment')
      ?.get('interventionId')
      ?.valueChanges.pipe(
        startWith(this.form.get('appointment')?.get('interventionId')?.value)
      )
      .subscribe((newInterventionId) => {
        this.currentIntervention = (this._fitter?.serviceTypes || []).find(
          (serviceType) => serviceType.id === newInterventionId
        );
        this.resetAppointmentType();
        this.changeDetector.markForCheck();
      });

    this.replacementVehicleNeeded = !!this.form
      .get('appointment')
      ?.get('isReplacementVehicleSelected')?.value;
    this.replacementVehicleSelected = !!this.form
      .get('appointment')
      ?.get('assignedReplacementVehicle')?.value;

    this.form?.updateValueAndValidity();
  }

  ngOnChanges() {
    this.checkVehicleAvailability();
  }

  resetAssignedRVUse() {
    this.getAppointmentForm()
      ?.get('assignedReplacementVehicleUse')
      ?.setValue(null);
  }

  resetAppointmentType() {
    let mobilityToSet: MobilityType =
      this.lastMobilityManuallySelected || 'garageIntervention';

    let isValidMobility: boolean;
    switch (mobilityToSet) {
      case 'garageIntervention':
        isValidMobility = this.canDoInCenterIntervention();
        break;
      case 'outsideIntervention':
        isValidMobility = this.canDoOutsideIntervention();
        break;
      case 'convoyageIntervention':
        isValidMobility = this.canDoConveyingIntervention();
        break;
      default:
        break;
    }
    if (!isValidMobility) {
      mobilityToSet = this.canDoInCenterIntervention()
        ? 'garageIntervention'
        : this.canDoOutsideIntervention()
        ? 'outsideIntervention'
        : 'convoyageIntervention';
    }

    this.getAppointmentForm()
      ?.get('outsideIntervention')
      ?.setValue(mobilityToSet === 'outsideIntervention');
    this.getAppointmentForm()
      ?.get('isConveying')
      ?.setValue(mobilityToSet === 'convoyageIntervention');
  }

  generateRVTimeSuggestions(min = '00:00', max = '23:59') {
    const resultSuggestions: string[] = [];

    const minMoment = dayjs(min, 'HH:mm').tz(
      this.timezoneService.getFitterTimezone(this._fitter),
      true
    );
    const maxMoment = dayjs(max, 'HH:mm').tz(
      this.timezoneService.getFitterTimezone(this._fitter),
      true
    );

    for (let i = 0; i < 24; i++) {
      if (minMoment.hour() > i || maxMoment.hour() < i) {
        continue;
      }

      for (let j = 0; j < 60; j += 15) {
        if (
          (minMoment.hour() === i && minMoment.minute() >= j) ||
          (maxMoment.hour() === i && maxMoment.minute() <= j)
        ) {
          continue;
        }

        resultSuggestions.push(`${i}:${j < 10 ? `0${j}` : j}`);
      }
    }

    return resultSuggestions;
  }

  generateRVEndTimeSuggestions() {
    const startDay = this.getRVUseStart();
    const endDay = this.getRVUseEnd();
    return startDay.isSame(endDay, 'day')
      ? this.generateRVTimeSuggestions(startDay.format('HH:mm'))
      : this.generateRVTimeSuggestions();
  }

  setCurrentFitter() {
    this._fitter = this._fitters.find(
      (fitter) =>
        fitter?.id === this.form.get('appointment')?.get('fitterId')?.value
    );
    this.updateFitterVehiclesAvailabilities();
  }

  updateFitterVehiclesAvailabilities() {
    if (this._fitter) {
      const formDossier = this.form.getRawValue() as Dossier;

      const currentVehicle: FitterVehicle =
        formDossier.appointment?.assignedReplacementVehicle;

      of(...(this._fitter?.vehicles || []))
        .pipe(
          filter((vehicle) => !!vehicle?.name),
          delay(100), // This delay allows us to not overload the CPU with too much searching in calendar events at the same time --> DO NOT REMOVE
          map((vehicle) => {
            const usedVehicle =
              vehicle.name === currentVehicle?.name &&
              vehicle.licensePlateNumber === currentVehicle?.licensePlateNumber
                ? currentVehicle
                : vehicle;

            const isActive =
              vehicle?.active &&
              !this.planningSandbox.isReplacementVehicleAlreadyUsed(
                formDossier,
                usedVehicle
              );

            if (isActive) {
              return {
                resource: vehicle,
                canUse: isActive,
              };
            } else {
              return null;
            }
          }),
          map((res) => res?.resource?.id + '##' + res?.resource?.name),
          scan((acc: string[], cur) => [...acc, cur], []),
          map((nameList) => nameList.filter((name) => !!name))
        )
        .subscribe((availableVehicles) => {
          const vehiclesAvailability = this._fitter?.vehicles
            ?.filter((vehicle) => !!vehicle?.name)
            .map((vehicle) => ({
              ...vehicle,
              currentActive: availableVehicles.includes(
                vehicle?.id + '##' + vehicle?.name
              ),
            }));

          if (
            formDossier?.appointment?.assignedReplacementVehicle &&
            this.getAppointmentForm().get('fitterId')?.value ===
              formDossier.appointment.fitterId &&
            !vehiclesAvailability
              .map((v) => v.id + '##' + v.name)
              .includes(
                formDossier?.appointment?.assignedReplacementVehicle.id +
                  '##' +
                  formDossier?.appointment?.assignedReplacementVehicle.name
              )
          ) {
            vehiclesAvailability.unshift(null, {
              ...formDossier.appointment.assignedReplacementVehicle,
              currentActive:
                formDossier.appointment.assignedReplacementVehicle.active,
            });
          } else {
            vehiclesAvailability.unshift(null);
          }

          this._fitter = {
            ...this._fitter,
            vehicles: vehiclesAvailability,
          };

          this.removeRVAlreadyInUse();
        });
    }
  }

  removeRVAlreadyInUse() {
    const currentRV: FitterVehicle = this.getAssignedVehicleFormValue();

    if (!currentRV) {
      return;
    }

    if (
      this.planningSandbox.isReplacementVehicleAlreadyUsed(
        {
          ...this.dossier,
          ...this.form.getRawValue(),
        },
        currentRV
      )
    ) {
      this.onReplacementVehicleSelect(null);

      this.showReplacementVehicleRemovedWarn();
    }
  }

  showReplacementVehicleRemovedWarn() {
    this.snackbarService.showBottomSnackBar(
      this.translator.getInstantTranslation(
        'PlanningForm.replacementVehicleRemovedWarn.title'
      ),
      this.translator.getInstantTranslation(
        'PlanningForm.replacementVehicleRemovedWarn.text'
      ),
      10,
      LoggerLevelType.WARNING
    );
  }

  checkVehicleAvailability() {
    const vehicleForm = this.getAssignedVehicleFormValue();
    if (vehicleForm) {
      const vehicle = this._fitter?.vehicles?.find(
        (vehicle) =>
          vehicle?.id === vehicleForm?.id && vehicle?.name === vehicleForm?.name
      );

      if (!vehicle) {
        this.selectedReplacementVehicle = null;
        this.getAssignedVehicleForm().setValue(null);
        this.getAssignedVehicleForm().setValidators([]);
        this.getInterventionLocationForm().updateValueAndValidity();
      } else if (!vehicle?.active) {
        this.selectedReplacementVehicle = null;
        this.isSelectedReplacementVehicleErrorEvent.emit(true);
        this.isSelectedReplacementVehicleError = true;

        this.getAssignedVehicleForm().setValue(null);
        this.getAssignedVehicleForm().setValidators([Validators.required]);
        this.getInterventionLocationForm().updateValueAndValidity();
      } else {
        this.selectedReplacementVehicle = vehicle;
      }
    } else if (
      !vehicleForm &&
      this.getAppointmentForm()?.get('isReplacementVehicleSelected')?.value ===
        true &&
      this.form.touched
    ) {
      this.selectedReplacementVehicle = null;
      this.isSelectedReplacementVehicleErrorEvent.emit(true);
      this.isSelectedReplacementVehicleError = true;
    }
  }

  onAddressChangeValue($event) {
    const value = $event?.target?.value;
    this.isAddressInputError = !value || value !== this.selectedAddressStr;
  }

  onChangeReplacementVehicle(checked: boolean) {
    if (!checked) {
      this.onReplacementVehicleSelect(null);

      if (
        this.getIsConveyingForm().value &&
        this.dossier.appointment?.outsideIntervention
      ) {
        this.getOutsideInterventionForm().setValue(true);
        this.getOutsideInterventionForm().updateValueAndValidity();
        this.getIsConveyingForm().setValue(false);
        this.getIsConveyingForm().updateValueAndValidity();
      }
    } else if (
      this.getOutsideInterventionForm().value &&
      !this.getIsConveyingForm().value
    ) {
      this.getOutsideInterventionForm().setValue(false);
      this.getOutsideInterventionForm().updateValueAndValidity();
      this.getIsConveyingForm().setValue(true);
      this.getIsConveyingForm().updateValueAndValidity();
    }
    this.getAppointmentForm()
      .get('isReplacementVehicleSelected')
      ?.setValue(checked);
    this.getAppointmentForm()?.updateValueAndValidity();
    this.changeDetector.markForCheck();
  }

  onReplacementVehicleSelect($event: {
    id: string;
    active: boolean;
    name: string;
    licensePlateNumber: string;
    manufacturer: string;
    model: string;
    currentActive: boolean;
  }) {
    if ($event?.name) {
      this.replacementVehicleSelected = !!$event;
      const { ...vehicule } = $event;
      this.isSelectedReplacementVehicleErrorEvent.emit(false);
      this.isSelectedReplacementVehicleError = false;
      this.selectedReplacementVehicle = vehicule;
      this.getAssignedVehicleForm()?.setValue(vehicule);
    } else {
      this.getAssignedVehicleForm()?.setValue(null);
    }
  }

  outsideInterventionChange(checkboxEvent: { value: boolean | 'convoyage' }) {
    let oldOutsideValue: boolean | 'convoyage' =
      !!this.getOutsideInterventionForm()?.value;
    if (this.getIsConveyingForm()?.value) {
      oldOutsideValue = 'convoyage';
    }

    if (checkboxEvent?.value === true) {
      // Only if outside selected
      this.getIsReplacementVehicleSelectedForm().setValue(false);
      this.getIsReplacementVehicleSelectedForm().updateValueAndValidity();

      this.getAssignedVehicleForm().setValue(null);
      this.getAssignedVehicleForm().clearValidators();
      this.getAssignedVehicleForm().updateValueAndValidity();
    } else {
      this.getAppointmentForm()
        ?.get('assignedInterventionVehicle')
        ?.setValue(null);
    }

    if (checkboxEvent?.value === false) {
      // Only if in center selected
      this.getInterventionLocationForm().clearValidators();
    } else {
      // Only if not in center (outside & conveying)
      this.getInterventionLocationForm().setValidators([Validators.required]);

      if (oldOutsideValue === false) {
        this.getInterventionLocationForm().setValue(null);
      }

      const currentInterventionLocation: LocationDataGeocoded =
        this.getInterventionLocationForm().value;

      if (
        !currentInterventionLocation ||
        currentInterventionLocation.placeId === this._fitter.google?.place_id
      ) {
        this.getInterventionLocationForm().setValue(
          this.form.get('customer')?.get('location')?.value
        );
      }
    }

    // Happens for any selection
    this.getIsConveyingForm().setValue(checkboxEvent?.value === 'convoyage');
    this.getOutsideInterventionForm().setValue(checkboxEvent?.value === true);
    this.getOutsideInterventionForm().updateValueAndValidity();
    this.getInterventionLocationForm().updateValueAndValidity();
    this.getIsConveyingForm().updateValueAndValidity();
    this.lastMobilityManuallySelected = this.getInterventionTypeValue();

    this.changeDetector.markForCheck();
  }

  getRVUseStart() {
    const formDossier: Dossier = this.form.getRawValue();
    const rvUsePeriod = getPeriodReplacementVehicleUse(formDossier, {
      start: formDossier.appointment.time || dayjs().format(),
      end: formDossier.appointment.endTime
        ? formDossier.appointment.endTime
        : formDossier.appointment.time
        ? dayjs(formDossier.appointment.time).add(1, 'hour').format()
        : dayjs().add(1, 'hour').format(),
    });

    return dayjs(rvUsePeriod.start ?? new Date()).tz(
      this.timezoneService.getFitterTimezone(this._fitter)
    );
  }

  getRVUseEnd() {
    const formDossier: Dossier = this.form.getRawValue();
    const rvUsePeriod = getPeriodReplacementVehicleUse(formDossier, {
      start: formDossier.appointment.time || dayjs().format(),
      end: formDossier.appointment.endTime
        ? formDossier.appointment.endTime
        : formDossier.appointment.time
        ? dayjs(formDossier.appointment.time).add(1, 'hour').format()
        : dayjs().add(1, 'hour').format(),
    });

    return dayjs(rvUsePeriod.end ?? this.getRVUseStart().add(1, 'hour')).tz(
      this.timezoneService.getFitterTimezone(this._fitter)
    );
  }

  onRVUseDateStartChange(event) {
    let newDate = dayjs(event?.value)
      .tz(this.timezoneService.getFitterTimezone(this._fitter), true)
      .clone();
    const previousDate = this.getRVUseStart();
    newDate = newDate.hour(previousDate.hour()).minute(previousDate.minute());

    let patchedValue = null;
    if (!newDate.isSame(this.getAppointmentFormValue()?.time, 'minutes')) {
      patchedValue = newDate.format();
    }
    const patchedValueMoment = dayjs(patchedValue).tz(
      this.timezoneService.getFitterTimezone(this._fitter)
    );

    const currentEndTime = this.getAppointmentForm().get(
      'assignedReplacementVehicleUse'
    )?.value?.end;
    const comparedEndTime =
      currentEndTime || this.getAppointmentForm().get('time')?.value;
    let newEndTime: string = null;
    if (
      !!comparedEndTime &&
      patchedValueMoment.isValid() &&
      patchedValueMoment.isAfter(comparedEndTime)
    ) {
      newEndTime = patchedValueMoment.add(15, 'minutes').format();
    } else if (currentEndTime) {
      newEndTime = currentEndTime;
    }

    this.getAppointmentForm().get('assignedReplacementVehicleUse')?.setValue({
      start: patchedValue,
      end: newEndTime,
    });

    this.updateFitterVehiclesAvailabilities();
  }

  onRVUseHourStartChange(value: string) {
    const previousDate = this.getRVUseStart();

    const newDate = dayjs(value, 'HH:mm')
      .tz(
        this._fitter?.timezone ||
          this.dossier.appointment?.timezone ||
          'Etc/UTC',
        true
      )
      .year(previousDate.year())
      .month(previousDate.month())
      .date(previousDate.date());

    let patchedValue = null;
    if (!newDate.isSame(this.getAppointmentFormValue()?.time, 'minutes')) {
      patchedValue = newDate.format();
    }

    this.getAppointmentForm()
      .get('assignedReplacementVehicleUse')
      ?.setValue({
        start: patchedValue,
        end: this.getAppointmentForm().get('assignedReplacementVehicleUse')
          ?.value?.end,
      });

    this.updateFitterVehiclesAvailabilities();
  }

  onRVUseDateEndChange(event) {
    let newDate = dayjs(event?.value).clone();
    const previousDate = this.getRVUseEnd();
    newDate = newDate.hour(previousDate.hour()).minute(previousDate.minute());

    let patchedValue = null;
    if (
      !newDate.isSame(
        this.getAppointmentFormValue()?.endTime ??
          this.getAppointmentFormValue()?.time,
        'minutes'
      )
    ) {
      patchedValue = newDate.format();
    }

    this.getAppointmentForm()
      .get('assignedReplacementVehicleUse')
      ?.setValue({
        start: this.getAppointmentForm().get('assignedReplacementVehicleUse')
          ?.value?.start,
        end: patchedValue,
      });

    this.updateFitterVehiclesAvailabilities();
  }

  onRVUseHourEndChange(value: string) {
    const previousDate = this.getRVUseEnd();
    const newDate = dayjs(value, 'HH:mm')
      .tz(
        this._fitter?.timezone ||
          this.dossier.appointment?.timezone ||
          'Etc/UTC',
        true
      )
      .year(previousDate.year())
      .month(previousDate.month())
      .date(previousDate.date());

    let patchedValue = null;
    if (
      !newDate.isSame(
        this.getAppointmentFormValue()?.endTime ??
          this.getAppointmentFormValue()?.time,
        'minutes'
      )
    ) {
      patchedValue = newDate.format();
    }

    this.getAppointmentForm()
      .get('assignedReplacementVehicleUse')
      ?.setValue({
        start: this.getAppointmentForm().get('assignedReplacementVehicleUse')
          ?.value?.start,
        end: patchedValue,
      });

    this.updateFitterVehiclesAvailabilities();
  }

  isRVPeriodError() {
    return !!this.getAppointmentForm().getError('rv_endtime_before_starttime');
  }

  isVehicleDisabled(option) {
    return option && !(option?.currentActive ?? option?.active);
  }

  vehicleCompareWith(vehicle1, vehicle2) {
    return vehicle1 && vehicle2
      ? vehicle1?.name === vehicle2?.name &&
          vehicle1?.licensePlateNumber === vehicle2?.licensePlateNumber
      : false;
  }

  canDoInCenterIntervention() {
    if (this.currentIntervention) {
      return this.planningSandbox.interventionAllowsInCenter(
        this.currentIntervention
      );
    } else {
      return this.planningSandbox.canDoInCenterIntervention(this._fitter);
    }
  }

  canDoOutsideIntervention() {
    if (this.currentIntervention) {
      return this.planningSandbox.interventionAllowsOutside(
        this.currentIntervention
      );
    } else {
      return this.planningSandbox.canDoOutsideIntervention(this._fitter);
    }
  }

  canDoConveyingIntervention() {
    if (this.currentIntervention) {
      return this.planningSandbox.interventionAllowsConveying(
        this.currentIntervention
      );
    } else {
      return this.planningSandbox.canDoConveyingIntervention(this._fitter);
    }
  }

  canShareReplacementVehicle() {
    return this.planningSandbox.canShareReplacementVehicle(this._fitter);
  }

  displayAddressForm() {
    return (
      (this.getOutsideInterventionForm().value &&
        this.canDoOutsideIntervention()) ||
      (this.canDoConveyingIntervention() && this.getIsConveyingForm().value)
    );
  }

  getInterventionTypeValue(): MobilityType {
    if (this.getAppointmentFormValue()?.isConveying) {
      return 'convoyageIntervention';
    }
    if (this.getAppointmentFormValue()?.outsideIntervention) {
      return 'outsideIntervention';
    }
    return 'garageIntervention';
  }

  getAppointmentForm() {
    return this.form.get('appointment') as FormGroup;
  }

  getAppointmentFormValue() {
    return this.form.get('appointment')?.value;
  }

  getIsReplacementVehicleSelectedForm() {
    return this.form
      .get('appointment')
      ?.get('isReplacementVehicleSelected') as AbstractControl;
  }

  getIsConveyingForm() {
    return this.form.get('appointment')?.get('isConveying') as AbstractControl;
  }

  getInterventionLocationForm() {
    return this.getAppointmentForm()?.get(
      'interventionLocation'
    ) as AbstractControl;
  }

  getOutsideInterventionForm() {
    return this.getAppointmentForm()?.get(
      'outsideIntervention'
    ) as AbstractControl;
  }

  getAssignedVehicleForm() {
    return this.getAppointmentForm()?.get(
      'assignedReplacementVehicle'
    ) as FormGroup;
  }

  getAssignedVehicleFormValue() {
    return this.getAssignedVehicleForm()?.value;
  }

  getInterventionLocation$() {
    return this.getInterventionLocationForm().valueChanges.pipe(
      startWith(this.getInterventionLocationForm().value)
    );
  }

  onChangeAddress(newAddress: LocationDataGeocoded) {
    this.getInterventionLocationForm()?.setValue(newAddress);
  }

  getLicensePlateAndManufacturer(vehicule: any) {
    let stringToDisplay = '';
    if (vehicule?.licensePlateNumber) {
      stringToDisplay += '- ' + vehicule.licensePlateNumber + ' ';
    }
    if (vehicule?.manufacturer) {
      stringToDisplay += '- ' + vehicule.manufacturer;
    }
    return stringToDisplay;
  }

  displayReplacementVehicleNeededAlert() {
    return (
      (!this.dossier?.id || !this.dossier?.appointment?.isValidatedByFitter) &&
      this.replacementVehicleNeeded &&
      !this.replacementVehicleSelected
    );
  }
}

type MobilityType =
  | 'outsideIntervention'
  | 'garageIntervention'
  | 'convoyageIntervention';
